import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Stack, FormControl, Checkbox } from '@chakra-ui/react';
import { toast } from 'react-toastify';
import {
    LoadingIndicator,
    Button,
    Heading,
    Text,
    ConfirmationDialog,
    Alert,
    AlertIcon,
    AlertDescription,
} from '@/components/v4';
import FormBase from '@/components/forms/new/FormBase';
import { stackSpacing, stackStyling } from '@/components/forms/new/formStyles';
import SRTRegionField from './formFields/SRTRegionField';
import SRTTrunkNameField from './formFields/SRTTrunkNameField';
import SRTChannelsField from './formFields/SRTChannelsField';
import SRTSipRegistrationSection from './formFields/SRTSipRegistrationSection';
import FormValidityWatcher from './formFields/FormValidityWatcher';
import useGetSRTTrunkService from './queries/useGetSRTTrunkService';
import useGetSRTTrunk from './queries/useGetSRTTrunk';
import { CHANNEL_TYPE_FIXED, AUTH_TYPE_SIP_REG } from '@/constants/srt';
import useCreateSRTTrunk from './queries/useCreateSRTTrunk';
import useUpdateSRTTrunk from './queries/useUpdateSRTTrunk';
import useDeleteSRTTrunk from './queries/useDeleteSRTTrunk';
import useGetSRTTrunkPassword from './queries/useGetSRTTrunkPassword';
import useSRTTrunkTypes from 'hooks/useSRTTrunkTypes';
import { SBCAAS_MODERN_LABEL } from '@/constants/sbcaas';

const EditSRTTrunk = () => {
    const { id } = useParams();
    const isAdd = !id;
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(!isAdd);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [regenerateOpen, setRegenerateOpen] = useState(false);
    const [regenerateAcknowledged, setRegenerateAcknowledged] = useState(false);
    const [availableRegions, setAvailableRegions] = useState([]);
    const [maxChannels, setMaxChannels] = useState(0);
    const [loadedTrunkType, setLoadedTrunkType] = useState(null);
    const [error, setError] = useState(null);
    const [canSave, setCanSave] = useState(false);

    const handleValidityChange = useCallback((valid) => setCanSave(valid), []);
    const [sipUsername, setSipUsername] = useState('');
    const [authPassword, setAuthPassword] = useState(location.state || null);

    // `/trunks/add/srt` and `/trunks/edit/srt/:id` render this same component,
    // so creating a trunk changes the route without remounting — the password
    // handed over in the navigation state has to be picked up here rather than
    // only in the initial state, or a freshly created trunk shows as masked.
    useEffect(() => {
        if (location.state) {
            setAuthPassword(location.state);
        }
    }, [location.state]);

    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const { state: modalState } = useSelector((state) => state.modal);

    const handleBackbutton = useCallback(() => {
        navigate('/trunks');
        dispatch({ type: 'RESET_MODAL' });
    }, [navigate, dispatch]);

    // Nothing to load in add mode, but any state left by a previous trunk has
    // to go before the blank form renders.
    useEffect(() => {
        if (isAdd) {
            dispatch({ type: 'RESET_MODAL' });
        }
    }, [isAdd, dispatch]);

    const createTrunk = useCreateSRTTrunk();
    const updateTrunk = useUpdateSRTTrunk();
    const deleteTrunk = useDeleteSRTTrunk();
    const generatePassword = useGetSRTTrunkPassword();

    useGetSRTTrunk({
        id,
        enabled: !isAdd,
        onSuccess: (trunk) => {
            setSipUsername(trunk.sipUsername || '');
            setLoadedTrunkType(trunk.trunkType || null);
            dispatch({ type: 'RESET_MODAL' });
            dispatch({ type: 'CHANGE_MODAL_STATE', payload: { ...trunk } });
            setIsLoading(false);
        },
        onError: (err) => {
            toast.error(
                err?.response?.data?.message || 'Failed to load trunk.',
            );
            setIsLoading(false);
        },
    });

    useGetSRTTrunkService({
        companyID: currentCompany,
        onSuccess: ({ regions, maxChannel }) => {
            setAvailableRegions(regions);
            setMaxChannels(maxChannel);
        },
    });

    // The trunk type SRT trunks are created against. In edit mode the loaded
    // trunk already carries its type, so no fetch is needed.
    const { srtTrunkTypeID, srtTrunkType } = useSRTTrunkTypes({
        enabled: isAdd,
    });

    // Channel counts are constrained by the trunk type, as on every other
    // trunk form — never assume 0 in increments of 1.
    const trunkType = isAdd ? srtTrunkType : loadedTrunkType;
    // A trunk with no channels is not a trunk, so the floor is 1 even when the
    // trunk type reports 0 or carries no minimum at all.
    const minChannels = Math.max(1, trunkType?.minChannels ?? 1);
    const incSize = trunkType?.incSize || 1;

    /**
     * The server's own message where there is one. A 500 rendered as an HTML
     * error page carries no message, and a response the browser blocks carries
     * no status either — say which happened instead of a bare "failed".
     */
    const describeError = (e, fallback) => {
        const serverMessage =
            e?.response?.data?.message || e?.response?.data?.title;
        if (serverMessage) return serverMessage;
        if (e?.response?.status) {
            return `${fallback} The server returned ${e.response.status}.`;
        }
        return `${fallback} No readable response was received from the server.`;
    };

    const failWith = (e, fallback) => {
        const message = describeError(e, fallback);
        setError(message);
        toast.error(message);
        setIsSaving(false);
    };

    const validateChannels = (channels, raw) => {
        if (!raw || channels <= 0) return 'Channels must be greater than zero';
        if (channels < minChannels) {
            return `Minimum channels is ${minChannels}`;
        }
        if (channels % incSize !== 0) {
            return `Channels must be in increments of ${incSize}`;
        }
        if (maxChannels > 0 && channels > maxChannels) {
            return `Max channels limit is ${maxChannels}`;
        }
        return false;
    };

    const missingFields = (values) => ({
        regionID: !values.regionID,
        name: !values.name?.trim(),
        channels: values.channels === '' || values.channels == null,
    });

    const validateFields = (values) => {
        const channels = parseInt(values.channels);
        return {
            regionID: !values.regionID && 'Region is required',
            name: !values.name?.trim()
                ? 'Trunk name is required'
                : values.name && !RegExp('^[a-zA-Z0-9- ]+$').test(values.name)
                  ? 'Must contain only alphanumeric characters'
                  : false,
            channels: validateChannels(channels, values.channels),
        };
    };

    const handleCreate = (values, { setErrors }) => {
        const errors = validateFields(values);
        if (Object.values(errors).some(Boolean)) {
            setErrors(errors);
            return;
        }
        if (!srtTrunkTypeID) {
            setError(
                `No ${SBCAAS_MODERN_LABEL} trunk type is available for this company.`,
            );
            return;
        }
        setError(null);
        setIsSaving(true);
        // Returned so the caller's await covers the create, not just the
        // validation ahead of it.
        return createTrunk
            .mutateAsync({
                trunkTypeID: srtTrunkTypeID,
                companyID: currentCompany,
                regionID: values.regionID,
                channelType: CHANNEL_TYPE_FIXED,
                channels: parseInt(values.channels),
                // SRT has no redundancy, but the model requires the field
                redundancyLevel: 1,
                name: values.name,
                authType: AUTH_TYPE_SIP_REG,
            })
            .then(async (trunk) => {
                // The trunk exists from here on — a failure fetching its
                // password must not be reported as a failed create.
                let passwordState = null;
                try {
                    passwordState = await generatePassword.mutateAsync(
                        trunk.id,
                    );
                } catch {
                    toast.warning(
                        'Trunk created, but its SIP password could not be generated. Use Regenerate Password.',
                    );
                }
                // Same component either side of this navigation, so the add
                // form's state has to be handed over explicitly.
                setIsSaving(false);
                setIsLoading(true);
                setAuthPassword(passwordState);
                navigate(`/trunks/edit/srt/${trunk.id}`, {
                    state: passwordState,
                });
            })
            .catch((e) => failWith(e, 'Failed to create trunk.'));
    };

    const handleUpdate = (values, { setErrors }) => {
        const errors = validateFields(values);
        if (Object.values(errors).some(Boolean)) {
            setErrors(errors);
            return;
        }
        const channels = parseInt(values.channels);
        setError(null);
        setIsSaving(true);
        return updateTrunk
            .mutateAsync({ id, name: values.name, channels })
            .then(() => {
                toast.success('Trunk updated successfully.');
                navigate('/trunks');
            })
            .catch((err) => failWith(err, 'Failed to update trunk.'))
            .finally(() => setIsSaving(false));
    };

    const handleDelete = () => {
        setIsDeleting(true);
        deleteTrunk
            .mutateAsync(id)
            .then(() => {
                dispatch({ type: 'RESET_MODAL' });
                dispatch({ type: 'RESET_TABLE' });
                toast.success(
                    `${modalState?.name} trunk deleted successfully.`,
                );
                navigate('/trunks');
            })
            .catch((err) => {
                setDeleteOpen(false);
                setError(describeError(err, 'Failed to delete trunk.'));
                setIsDeleting(false);
            });
    };

    const closeRegenerate = () => {
        setRegenerateOpen(false);
        setRegenerateAcknowledged(false);
    };

    const handleRegeneratePassword = () => {
        closeRegenerate();
        setIsRegenerating(true);
        generatePassword
            .mutateAsync(id)
            .then((password) => setAuthPassword(password))
            .catch((err) =>
                setError(describeError(err, 'Failed to regenerate password.')),
            )
            .finally(() => setIsRegenerating(false));
    };

    const breadCrumbTrail = [
        { label: 'Trunks', path: '/trunks' },
        {
            label: isAdd ? `Add ${SBCAAS_MODERN_LABEL} Trunk` : 'Edit Trunk',
            path: '',
        },
    ];

    // Matches the Back / Update pair every other trunk add and edit form uses,
    // rather than FormBase's default Save / Back.
    const trunkFormButtons = (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                gap: 2,
                justifyContent: 'flex-start',
            }}>
            <Button
                variant="outline"
                sx={{ background: 'white', minWidth: '120px' }}
                onClick={handleBackbutton}>
                Back
            </Button>
            <Button
                type="submit"
                sx={{ minWidth: '120px' }}
                isDisabled={!canSave}
                isLoading={isSaving}>
                {isAdd ? 'Create' : 'Update'}
            </Button>
        </Box>
    );

    if (isLoading) return <LoadingIndicator />;

    const errorAlert = error && (
        <Alert status="error" mt={6}>
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
        </Alert>
    );

    const generalTab = ({
        values,
        touched,
        dirty,
        setFieldValue,
        setFieldTouched,
    }) => {
        const fieldErrors = validateFields(values);
        const missing = missingFields(values);
        // The border marks a required field left empty, and only once the user
        // has visited it — a form not yet filled in is not "wrong". Other rule
        // violations still lock Save, but do not paint the field red.
        const showInvalid = (name) => Boolean(touched[name] && missing[name]);

        return (
            <>
                <Heading
                    as="h3"
                    fontSize="lg"
                    fontWeight={600}
                    mb={6}
                    color={darkMode ? 'white' : '#262626'}>
                    General Settings
                </Heading>

                <SRTRegionField
                    value={values.regionID}
                    options={availableRegions}
                    // A provisioned trunk cannot move region
                    isReadOnly={!isAdd}
                    isInvalid={showInvalid('regionID')}
                    onBlur={() => setFieldTouched('regionID', true)}
                    onChange={(regionID) => {
                        setFieldTouched('regionID', true);
                        setFieldValue('regionID', regionID);
                    }}
                />
                <SRTTrunkNameField isInvalid={showInvalid('name')} />
                <SRTChannelsField
                    isInvalid={showInvalid('channels')}
                    minChannels={minChannels}
                    incSize={incSize}
                    maxChannels={maxChannels}
                />

                {/* Credentials only exist once the trunk has been created */}
                {!isAdd && (
                    <SRTSipRegistrationSection
                        authPassword={authPassword}
                        isRegenerating={isRegenerating}
                        onRegenerate={() => setRegenerateOpen(true)}
                    />
                )}

                {errorAlert}

                {/* Nothing to save until something has changed, and never
                    while a field is invalid */}
                <FormValidityWatcher
                    canSave={dirty && !Object.values(fieldErrors).some(Boolean)}
                    onChange={handleValidityChange}
                />
            </>
        );
    };

    const adminTab = (
        <>
            <FormControl sx={{ margin: '0 !important' }}>
                <Stack
                    direction="row"
                    spacing={stackSpacing}
                    sx={stackStyling()}
                    alignItems="center">
                    <Box sx={{ minWidth: '246px' }}>
                        <Text fontWeight="bold">Delete</Text>
                        <Text>
                            By deleting the trunk, it will remove all trunk
                            configuration and calls will stop processing
                        </Text>
                    </Box>
                    <Button
                        colorScheme="red"
                        onClick={() => setDeleteOpen(true)}>
                        Delete
                    </Button>
                </Stack>
            </FormControl>
            {errorAlert}
        </>
    );

    return (
        <>
            <FormBase
                tabMode
                breadCrumbTrail={breadCrumbTrail}
                header={
                    isAdd
                        ? `Add ${SBCAAS_MODERN_LABEL} Trunk`
                        : modalState?.sipHeader || modalState?.name
                }
                initialValues={{
                    regionID: modalState?.regionID || '',
                    name: modalState?.name || '',
                    channels: modalState?.channels ?? '',
                    sipUsername: sipUsername || '',
                    password: authPassword || '••••••••',
                }}
                enableReinitialize
                finalFormSubmit={isAdd ? handleCreate : handleUpdate}
                isLoading={isSaving}
                isSubmitDisabled={!canSave}
                customClick
                onBack={handleBackbutton}
                // Own footer, worded and ordered like every other trunk form
                hideSubmitButton
                noBackBtn
                extraButton={trunkFormButtons}>
                {(formProps) =>
                    [
                        { label: 'General', content: generalTab(formProps) },
                        !isAdd && { label: 'Admin', content: adminTab },
                    ].filter(Boolean)
                }
            </FormBase>

            <ConfirmationDialog
                isOpen={regenerateOpen}
                onClose={closeRegenerate}
                onConfirm={handleRegeneratePassword}
                title="Regenerate Password"
                message={
                    <>
                        <Text mb={4}>
                            Please ensure you have taken note of the password.
                            Generating a new password will require you to
                            reconfigure any devices using this trunk. Navigating
                            away from this page will clear your password from
                            the screen.
                        </Text>
                        <Checkbox
                            isChecked={regenerateAcknowledged}
                            onChange={(e) =>
                                setRegenerateAcknowledged(e.target.checked)
                            }>
                            I understand any devices using this trunk must be
                            reconfigured
                        </Checkbox>
                    </>
                }
                confirmText="Regenerate"
                isConfirmDisabled={!regenerateAcknowledged}
                isLoading={isRegenerating}
            />

            <ConfirmationDialog
                isOpen={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                onConfirm={handleDelete}
                title="Delete Trunk"
                message={`Are you sure you want to delete "${modalState?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                confirmColorScheme="red"
                isLoading={isDeleting}
            />
        </>
    );
};

export default EditSRTTrunk;
